import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import {
  WebsiteRequest,
  WebsiteResponse,
  CheckSubdomainRequest,
  SaveCustomDomainResponse,
  CustomDomainDiagnosticResponse,
  VerifyCustomDomainResponse,
  ValidateCustomDomainResponse,
  DeleteCustomDomainResponse
} from '../../models/website-builder';
import { HttpErrorResponse } from '@angular/common/http';


@Injectable({
  providedIn: 'root'
})
export class WebsiteBuilderService {
  constructor(private http: ApplicationHttpClient) { }

  /**
   * Lightweight check if AI chatbot is enabled (for sidebar navigation)
   * Returns only boolean, not full website object
   */
  checkAiChatbotEnabled(): Observable<boolean> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = `${Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.Website(), jwt.orgId)}/ai-chatbot-status`;

    return this.http.get<{ enabled: boolean }>(url).pipe(
      map(response => response.enabled),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ Error checking AI chatbot status:', error);
        return of(false); // Default to false if error
      })
    );
  }

  /**
   * Get the website for the current organization
   */
  getWebsite(): Observable<WebsiteResponse | null> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.Website(), jwt.orgId);

    return this.http.get<WebsiteResponse>(url).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('❌ Error fetching website:', error);
        if (error.status === 404) {
          return of(null);
        }
        throw error;
      })
    );
  }


  /**
   * Create a new website
   */
  createWebsite(data: WebsiteRequest): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.Website(), jwt.orgId);
    return this.http.post<WebsiteResponse>(url, JSON.stringify(data));
  }

  /**
   * Update an existing website
   */
  updateWebsite(id: number, data: Partial<WebsiteRequest>): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.WebsiteById(id), jwt.orgId);

    return this.http.put<WebsiteResponse>(url, JSON.stringify(data));
  }

  /**
   * Publish the website
   */
  publishWebsite(id: number): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.PublishWebsite(id), jwt.orgId);
    return this.http.post<WebsiteResponse>(url, JSON.stringify({}));
  }

  /**
   * Check if subdomain is available
   */
  checkSubdomainAvailability(subdomain: string): Observable<{ available: boolean }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.CheckSubdomain(), jwt.orgId);
    const data: CheckSubdomainRequest = { subdomain };
    return this.http.post<{ available: boolean }>(url, JSON.stringify(data));
  }

  /**
   * Check if custom domain is available (not used by another website)
   */
  checkCustomDomainAvailability(domain: string): Observable<ValidateCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/validate', jwt.orgId);
    const data = { domain };
    return this.http.post<ValidateCustomDomainResponse>(url, JSON.stringify(data))
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Custom domain validation failed:', error);
          // On error, don't block the user - let them continue
          return of({ available: true, message: 'Could not validate domain availability' });
        })
      );
  }

  /**
   * Save custom domain to website
   */
  saveCustomDomain(domain: string): Observable<SaveCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/save', jwt.orgId);
    const data = { domain };
    return this.http.post<SaveCustomDomainResponse>(url, JSON.stringify(data));
  }

  /**
   * Delete custom domain from website
   */
  deleteCustomDomain(): Observable<DeleteCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/delete', jwt.orgId);
    return this.http.post<DeleteCustomDomainResponse>(url, JSON.stringify({}));
  }

  /**
   * Verify custom domain DNS configuration
   */
  verifyCustomDomain(): Observable<VerifyCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/verify', jwt.orgId);
    return this.http.post<VerifyCustomDomainResponse>(url, JSON.stringify({}));
  }

  /**
   * Super-only read-only diagnostic of the custom domain
   */
  diagnoseCustomDomain(): Observable<CustomDomainDiagnosticResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/diagnose', jwt.orgId);
    return this.http.get<CustomDomainDiagnosticResponse>(url);
  }

  /**
   * Get SSL validation records for existing certificate (does NOT request new certificate)
   */
  getSslValidationRecords(): Observable<SaveCustomDomainResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/custom-domain/ssl-validation-records', jwt.orgId);
    return this.http.get<SaveCustomDomainResponse>(url);
  }

  /**
   * Upload images for website content
   */
  uploadImage(file: File, section: string): Observable<{ url: string }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/upload-image', jwt.orgId);
    const formData = new FormData();
    formData.append('image', file);
    formData.append('section_type', section);  // Fixed to match backend expectation
    return this.http.http.post<{ url: string }>(url, formData);
  }

  /**
   * Save comprehensive website data including sections
   */
  saveWebsite(data: Partial<WebsiteRequest>): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/save', jwt.orgId);

    return this.http.post<WebsiteResponse>(url, JSON.stringify(data));
  }

  /**
   * Unified save for general settings + sections (AI chatbot stays separate)
   * Used for: draft saves, tab switching auto-save, and preview (with preview=true flag)
   * AI chatbot disabled by default for new customers - separate workflow
   */
  saveGeneralAndSections(data: Partial<WebsiteRequest>, preview: boolean = false): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/save', jwt.orgId);

    const requestData = {
      ...data,
      preview: preview  // Only set preview flag if requested
    };


    return this.http.post<any>(url, JSON.stringify(requestData)).pipe(
      map(response => {
        // UNIFIED RESPONSE HANDLING: Both preview and normal saves now return the same structure
        // API always returns { success, message, data } format
        return response.data ? response.data as WebsiteResponse : response as WebsiteResponse;
      })
    );
  }

  /**
   * Save AI chatbot settings
   */
  saveAIChatbotSettings(data: { enabled: boolean; linkedDocuments: any[] }): Observable<WebsiteResponse> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/save-ai-chatbot', jwt.orgId);

    const requestData = {
      ai_chatbot: {
        enabled: data.enabled,
        linkedDocuments: data.linkedDocuments
      }
    };

    return this.http.post<any>(url, JSON.stringify(requestData)).pipe(
      map(response => {
        // API returns { success, message, data } format
        return response.data as WebsiteResponse;
      })
    );
  }

  /**
   * Remove a specific document from AI chatbot settings (real-time operation)
   */
  removeAIChatbotDocument(documentId: number): Observable<{ success: boolean; message: string; data: any }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeLegFiCoreUrl(`/api/organizations/${jwt.orgId}/website-builder/ai-chatbot/document/${documentId}`);

    return this.http.delete<{ success: boolean; message: string; data: any }>(url);
  }

  /**
   * Clear all document memories from AI chatbot (pgvector database)
   * This removes all learned content from uploaded documents
   */
  clearAllAIChatbotDocumentMemories(): Observable<{ success: boolean; message: string; data: any }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeLegFiCoreUrl(`/api/organizations/${jwt.orgId}/website-builder/ai-chatbot/clear-memories`);

    return this.http.delete<{ success: boolean; message: string; data: any }>(url);
  }

  /**
   * Add new documents to AI chatbot settings (real-time operation)
   */
  addAIChatbotDocuments(documents: any[]): Observable<{ success: boolean; message: string; data: any }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeLegFiCoreUrl(`/api/organizations/${jwt.orgId}/website-builder/ai-chatbot/documents`);

    const requestData = {
      documents: documents
    };

    return this.http.post<{ success: boolean; message: string; data: any }>(url, JSON.stringify(requestData));
  }

  /**
   * Upload a document file to organization storage (creates OrganizationFile record)
   */
  uploadDocumentFile(file: File): Observable<{ success: boolean; message: string; data: any }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = Routes.MakeWebsiteBuilderModuleUrl('/upload-document', jwt.orgId);

    const formData = new FormData();
    formData.append('document', file);

    return this.http.http.post<{ success: boolean; message: string; data: any }>(url, formData);
  }


  /**
   * Start building website - changes status from 'initialized' to 'draft'
   * ONLY controlled way to transition from never-started to actively-building
   */
  startBuilding(websiteId: number): Observable<{ success: boolean; message: string; status: string }> {
    const jwt: JwtLegFiClaims = LegFiJwtService.read();
    const url = `${Routes.MakeWebsiteBuilderModuleUrl(Routes.WebsiteBuilderModule.Website(), jwt.orgId)}/${websiteId}/start-building`;

    return this.http.patch<{ success: boolean; message: string; status: string }>(url, null);
  }

}
